import Phaser from 'phaser';
import { coinLabel } from '../../config/coinLabel';
import { LAST_LEVEL_ID, WIN_GATE_LEVEL_IDS } from '../../config/levels';
import { ProgressService } from '../../services/progressService';
import { saveStorage } from '../saveStorage';
import { openCapsule, openGoldenBag, openTakeoutBag } from '../bagOpening';
import { openVarietyOrb } from '../varietyOrbOpening';
import { chestRandom, openChest } from '../chestOpening';
import { CHEST_ART, CHEST_NAMES, rollChestPrize, type ChestKind } from '../../config/originChests';
import { spinCoin } from '../coin';
import { getAudioService } from '../audio/AudioService';
import { addMuteButton } from '../audio/audioControls';
import { addFullscreenButton } from '../screenControls';
import { installDebugReturn } from '../debugMode';
import { POTIONS, type PotionKind } from '../../config/potions';
import { canApply, formatRemaining } from '../../config/effects';
import { FUNGI_ITEMS, type FungiItemKind } from '../../config/fungiItems';
import { RARITY_COLORS } from '../../config/restaurant';
import {
  LEGENDARY_YELLOW,
  LEGENDARY_YELLOW_HEX,
  POTION_GREEN,
  POTION_GREEN_HEX,
  showUseDialog,
  usePotion,
  flashHint,
} from '../hubShared';

const GOLD = 0xf5c518;
const GOLD_HEX = '#f5c518';
/** The Variety Orb's slot and dialog border — purple, so it never reads as a bag. */
const ORB_PURPLE = 0xc77dff;
/** Space Debris: cold steel, so the slab never reads as a potion or a gem. */
const DEBRIS_STEEL = 0x8fa3b8;
/** The Moon Stone's outline: the blue-violet of the stone itself. */
const MOONSTONE_VIOLET = 0x9d7bff;
/** The Golden Stone's outline: the stone's own gold, a shade warmer than a bag's. */
const GOLDSTONE_GOLD = 0xffb81c;
const GOLDSTONE_GOLD_HEX = '#ffd36b';
/** The Capystone's outline: capybara brown, the Capycoin's own warm tone. */
const CAPYSTONE_BROWN = 0xa8703a;
const CAPYSTONE_BROWN_HEX = '#e0b98a';
/** The Dark Shard's outline: the crystal's own crimson. */
const DARKSHARD_RED = 0xd8263a;
const DARKSHARD_RED_HEX = '#ff7a86';
/** The Light Shard's frame: the Dark Shard's, in pale sky white. */
const LIGHTSHARD_WHITE = 0xbfe3ff;
const LIGHTSHARD_WHITE_HEX = '#eaf6ff';
/** The Evil Orb's outline: its molten orange-red. */
const EVIL_ORB_ORANGE = 0xff5a1f;
/** The Token of Shroom's slot: mossy green (spec 2026-10-27). */
const TOKEN_MOSS = 0x8fbf3a;
/** The Light Orb's pale sky blue, and the Origin chests' rims (spec 2026-10-13). */
const LIGHT_ORB_BLUE = 0x9fd8ff;
const DARK_CHEST_PURPLE = 0x8a6ad0;
const DARK_CHEST_PURPLE_HEX = '#d8c8ff';
const LIGHT_CHEST_CREAM = 0xfff1c8;
const LIGHT_CHEST_CREAM_HEX = '#fff6dc';
/** The Fungi Forest's items (spec 2026-10-24): the Shroom's red, the Snowshroom's ice. */
const SHROOM_RED = 0xe0483a;
const SHROOM_RED_HEX = '#ff9a8a';
const SNOWSHROOM_ICE = 0x9fd6f0;
const SNOWSHROOM_ICE_HEX = '#d6f0ff';
/**
 * Ketchuptastic — the top band (restaurant.ts RARITY_COLORS). Both orbs carry
 * it: the Variety Orb since 2026-10-07, the Evil Orb from the start.
 */
const KETCHUP_HEX = `#${RARITY_COLORS.Ketchuptastic.toString(16).padStart(6, '0')}`;
const DEBRIS_STEEL_HEX = '#8fa3b8';
/** The Capybara Event's capsule: its own warm brown, never a gold bag. */
/** The Golden Takeout Bag's slot — a brighter gold than the Normal bags' frame. */
const GOLDEN_BAG_GOLD = 0xffd21f;
const GOLDEN_BAG_GOLD_HEX = '#ffd21f';
const CAPSULE_BROWN = 0xc08a4a;
const CAPSULE_BROWN_HEX = '#e0b98a';

/** Bag grid: 6 across, 3 rows visible, laid out under the header. */
const CELL = 150;
const COLS = 6;
const GAP_X = 22;
const GAP_Y = 18;
const GRID_TOP = 190;
const CELL_XS = ((): number[] => {
  const span = COLS * CELL + (COLS - 1) * GAP_X;
  const first = 640 - span / 2 + CELL / 2;
  return Array.from({ length: COLS }, (_, c) => first + c * (CELL + GAP_X));
})();
/** Rows drawn before the grid stops (18 bags shown; the rest are counted, not drawn). */
const MAX_ROWS = 3;

/**
 * StorageScene — the shelf of ready-but-unopened takeout bags.
 *
 * Bags land here from the Restaurant's STORE button. Clicking one offers OPEN
 * (which runs the same roulette the Restaurant does) or CANCEL. Reached from the
 * grey STORAGE button on the episode screen; BACK returns there.
 *
 * Towers themselves live in the Equip screen; this shelf holds unopened
 * containers only.
 *
 * The bags are still a COUNT rather than a list, because every one of them is
 * an interchangeable Normal Takeout Bag. The Variety Orb — the Level 25 prize —
 * is the one exception: it is a single distinct item, so it takes the first
 * slot by hand and has its own dialog, rather than the count being widened
 * into a tally per type. That remains the right change to make on the day a
 * SECOND bag type exists, or a second orb; one special item does not earn a
 * general inventory.
 */
export class StorageScene extends Phaser.Scene {
  private progress!: ProgressService;
  private balanceText!: Phaser.GameObjects.Text;
  private countText!: Phaser.GameObjects.Text;
  /** Grid + empty-state, rebuilt whenever the bag count changes. */
  private gridLayer?: Phaser.GameObjects.Container;
  /** True while the open dialog or the roulette is up. */
  private busy = false;
  /** Where BACK goes — the hub chapter screen when we came from The Hub. */
  private from = 'LevelSelectScene';

  constructor() {
    super({ key: 'StorageScene' });
  }

  init(data: { from?: string }): void {
    this.from = data?.from ?? 'LevelSelectScene';
  }

  create(): void {
    this.progress = new ProgressService(saveStorage(), LAST_LEVEL_ID, WIN_GATE_LEVEL_IDS);
    // The Capybara Event closed (2026-10-06): a capsule still on order has
    // no OPEN / STORE button any more, so it waits on this shelf instead.
    this.progress.shelvePendingCapsule();
    this.gridLayer = undefined;
    this.busy = false;

    this.add.image(640, 360, 'bg-shop').setDisplaySize(1280, 720);
    this.add.rectangle(640, 360, 1280, 720, 0x000000, 0.6);

    this.add
      .text(640, 44, 'STORAGE', { fontSize: '38px', color: GOLD_HEX, fontStyle: 'bold' })
      .setOrigin(0.5);
    this.add
      .text(640, 80, 'TAKEOUT BAGS YOU HAVEN’T OPENED YET', {
        fontSize: '15px',
        color: '#c8d2d8',
      })
      .setOrigin(0.5)
      .setShadow(0, 2, '#000000', 4);

    const backBtn = this.add
      .text(80, 50, '← BACK', {
        fontSize: '22px',
        color: '#cfe0bd',
        backgroundColor: '#15131f',
        padding: { x: 16, y: 12 },
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    backBtn.on('pointerover', () => backBtn.setColor(GOLD_HEX));
    backBtn.on('pointerout', () => backBtn.setColor('#cfe0bd'));
    backBtn.on('pointerup', () => {
      if (this.busy) return;
      this.scene.start(this.from);
    });

    const coinIcon = this.add.image(960, 50, 'coin').setDisplaySize(34, 34).setDepth(5);
    spinCoin(this, coinIcon);
    this.balanceText = this.add
      .text(982, 50, coinLabel(this.progress.coins()), {
        fontSize: '22px',
        color: GOLD_HEX,
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5)
      .setDepth(5);

    addMuteButton(this, 1245, 50, getAudioService(this));
    addFullscreenButton(this, 1100, 50);

    this.countText = this.add
      .text(640, 132, '', { fontSize: '22px', color: '#ffffff', fontStyle: 'bold' })
      .setOrigin(0.5);

    this.render();
    installDebugReturn(this); // Esc → DemoScene when launched via ?debug
  }

  /** (Re)draw the grid from the stored bag count, plus the orb if it is here. */
  private render(): void {
    this.gridLayer?.destroy();
    const layer = this.add.container(0, 0).setDepth(6);
    this.gridLayer = layer;

    const total = this.progress.storedBags();
    // The Variety Orb takes the FIRST slot when it is on the shelf, and the
    // bags fill in after it. It is the one distinct item Storage holds — see
    // the note on the class — so it is placed by hand rather than by widening
    // the bag count into a list.
    const hasOrb = this.progress.orbState() === 'stored';
    // The Hub's items: the legendary Diamond takes the very first slot, then
    // any potions bought and stored, then the orb, then the bags.
    const hasDiamond = this.progress.diamondState() === 'stored';
    const hasMoonstone = this.progress.moonstoneState() === 'stored';
    const debris = this.progress.storedDebris();
    const goldstones = this.progress.storedGoldstones();
    const capystones = this.progress.storedCapystones();
    const darkShards = this.progress.storedDarkShards();
    const lightShards = this.progress.storedLightShards();
    const hasEvilOrb = this.progress.evilOrbState() === 'stored';
    const hasLightOrb = this.progress.lightOrbState() === 'stored';
    const hasToken = this.progress.tokenOfShroomState() === 'stored';
    const darkChests = this.progress.storedChests('dark');
    const lightChests = this.progress.storedChests('light');
    const shrooms = this.progress.storedFungiItems('shroom');
    const snowshrooms = this.progress.storedFungiItems('snowshroom');
    const potions = this.progress.storedPotions();
    const capsules = this.progress.storedCapsules();
    const goldenBags = this.progress.storedGoldenBags();
    const parts = [`BAGS: ${total}`];
    if (goldenBags > 0) parts.push(`GOLDEN BAGS: ${goldenBags}`);
    if (hasOrb) parts.push('ORB: 1');
    if (hasDiamond) parts.push('DIAMOND: 1');
    if (hasMoonstone) parts.push('MOON STONE: 1');
    if (goldstones > 0) parts.push(`GOLDEN STONES: ${goldstones}`);
    if (capystones > 0) parts.push(`CAPYSTONES: ${capystones}`);
    if (darkShards > 0) parts.push(`DARK SHARDS: ${darkShards}`);
    if (lightShards > 0) parts.push(`LIGHT SHARDS: ${lightShards}`);
    if (hasEvilOrb) parts.push('EVIL ORB: 1');
    if (hasLightOrb) parts.push('LIGHT ORB: 1');
    if (hasToken) parts.push('TOKEN OF SHROOM: 1');
    if (darkChests > 0) parts.push(`DARK CHESTS: ${darkChests}`);
    if (lightChests > 0) parts.push(`LIGHT CHESTS: ${lightChests}`);
    if (shrooms > 0) parts.push(`SHROOMS: ${shrooms}`);
    if (snowshrooms > 0) parts.push(`SNOWSHROOMS: ${snowshrooms}`);
    if (debris > 0) parts.push(`DEBRIS: ${debris}`);
    if (potions.length) parts.push(`POTIONS: ${potions.length}`);
    if (capsules > 0) parts.push(`CAPSULES: ${capsules}`);
    this.countText.setText(parts.join(' · '));

    if (
      total === 0 &&
      !hasOrb &&
      !hasDiamond &&
      !hasMoonstone &&
      goldstones === 0 &&
      capystones === 0 &&
      darkShards === 0 &&
      lightShards === 0 &&
      !hasEvilOrb &&
      !hasLightOrb &&
      !hasToken &&
      darkChests === 0 &&
      lightChests === 0 &&
      shrooms === 0 &&
      snowshrooms === 0 &&
      debris === 0 &&
      potions.length === 0 &&
      capsules === 0 &&
      goldenBags === 0
    ) {
      layer.add(
        this.add
          .text(640, 340, 'Storage is empty.', {
            fontSize: '24px',
            color: '#9aa6ad',
            fontStyle: 'bold',
          })
          .setOrigin(0.5),
      );
      layer.add(
        this.add
          .text(640, 380, 'Order a bag in the Restaurant and press STORE to keep it for later.', {
            fontSize: '16px',
            color: '#b8c4c6',
            fontStyle: 'italic',
          })
          .setOrigin(0.5),
      );
      return;
    }

    /** Grid cell -> screen position, so the orb and the bags share one layout. */
    const cell = (i: number) => ({
      x: CELL_XS[i % COLS],
      y: GRID_TOP + Math.floor(i / COLS) * (CELL + GAP_Y) + CELL / 2,
    });
    const slotBox = (i: number, name: string, stroke: number) => {
      const { x, y } = cell(i);
      const box = this.add
        .rectangle(x, y, CELL, CELL, 0x1b1830, 0.92)
        .setStrokeStyle(2, stroke, 0.55)
        .setInteractive({ useHandCursor: true })
        .setName(name);
      layer.add(box);
      box.on('pointerover', () => box.setStrokeStyle(3, stroke, 1));
      box.on('pointerout', () => box.setStrokeStyle(2, stroke, 0.55));
      return box;
    };

    let offset = 0;
    if (hasDiamond) {
      const { x, y } = cell(offset);
      const box = slotBox(offset, 'storage-diamond', LEGENDARY_YELLOW);
      // Legendary: a soft glow behind the gem on top of the yellow outline.
      const glow = this.add
        .rectangle(x, y, CELL - 6, CELL - 6, LEGENDARY_YELLOW, 0.12)
        .setStrokeStyle(4, LEGENDARY_YELLOW, 0.35);
      layer.add(glow);
      layer.add(this.add.image(x, y - 10, 'diamond').setDisplaySize(90, 90));
      layer.add(
        this.add
          .text(x, y + CELL / 2 - 16, 'Diamond · LEGENDARY', {
            fontSize: '12px',
            color: LEGENDARY_YELLOW_HEX,
            fontStyle: 'bold',
          })
          .setOrigin(0.5),
      );
      box.on('pointerup', () => {
        if (this.busy) return;
        this.showDiamondDialog();
      });
      offset += 1;
    }
    // The Moon Stone, straight after the Diamond: the other one-of-a-kind.
    if (hasMoonstone) {
      const { x, y } = cell(offset);
      const box = slotBox(offset, 'storage-moonstone', MOONSTONE_VIOLET);
      const glow = this.add
        .rectangle(x, y, CELL - 6, CELL - 6, MOONSTONE_VIOLET, 0.12)
        .setStrokeStyle(4, MOONSTONE_VIOLET, 0.35);
      layer.add(glow);
      if (this.textures.exists('item-moonstone')) {
        layer.add(this.add.image(x, y - 10, 'item-moonstone').setDisplaySize(90, 90));
      }
      layer.add(
        this.add
          .text(x, y + CELL / 2 - 16, 'Moon Stone · LEGENDARY', {
            fontSize: '12px',
            color: '#c3aaff',
            fontStyle: 'bold',
          })
          .setOrigin(0.5),
      );
      box.on('pointerup', () => {
        if (this.busy) return;
        this.showMoonstoneDialog();
      });
      offset += 1;
    }
    // Golden Stones: King Hambi pays one per K–O, so they STACK — one slot
    // with the count on it, like Space Debris.
    if (goldstones > 0) {
      const { x, y } = cell(offset);
      const box = slotBox(offset, 'storage-goldstone', GOLDSTONE_GOLD);
      const glow = this.add
        .rectangle(x, y, CELL - 6, CELL - 6, GOLDSTONE_GOLD, 0.12)
        .setStrokeStyle(4, GOLDSTONE_GOLD, 0.35);
      layer.add(glow);
      if (this.textures.exists('item-goldstone')) {
        layer.add(this.add.image(x, y - 10, 'item-goldstone').setDisplaySize(90, 90));
      }
      layer.add(
        this.add
          .text(x, y + CELL / 2 - 16, `Golden Stone · ${goldstones}`, {
            fontSize: '13px',
            color: GOLDSTONE_GOLD_HEX,
            fontStyle: 'bold',
          })
          .setOrigin(0.5),
      );
      box.on('pointerup', () => {
        if (this.busy) return;
        this.showGoldstoneDialog();
      });
      offset += 1;
    }
    // Capystones: one per finished archive Capy Run, so they STACK too.
    if (capystones > 0) {
      const { x, y } = cell(offset);
      const box = slotBox(offset, 'storage-capystone', CAPYSTONE_BROWN);
      const glow = this.add
        .rectangle(x, y, CELL - 6, CELL - 6, CAPYSTONE_BROWN, 0.12)
        .setStrokeStyle(4, CAPYSTONE_BROWN, 0.35);
      layer.add(glow);
      if (this.textures.exists('item-capystone')) {
        layer.add(this.add.image(x, y - 10, 'item-capystone').setDisplaySize(90, 90));
      }
      layer.add(
        this.add
          .text(x, y + CELL / 2 - 16, `Capystone · ${capystones}`, {
            fontSize: '13px',
            color: CAPYSTONE_BROWN_HEX,
            fontStyle: 'bold',
          })
          .setOrigin(0.5),
      );
      box.on('pointerup', () => {
        if (this.busy) return;
        this.showCapystoneDialog();
      });
      offset += 1;
    }
    // Dark Shards: one per Dark Forest win, so they STACK — one slot, counted.
    if (darkShards > 0) {
      const { x, y } = cell(offset);
      const box = slotBox(offset, 'storage-darkshard', DARKSHARD_RED);
      const glow = this.add
        .rectangle(x, y, CELL - 6, CELL - 6, DARKSHARD_RED, 0.12)
        .setStrokeStyle(4, DARKSHARD_RED, 0.35);
      layer.add(glow);
      if (this.textures.exists('item-darkshard')) {
        layer.add(this.add.image(x, y - 10, 'item-darkshard').setDisplaySize(90, 90));
      }
      layer.add(
        this.add
          .text(x, y + CELL / 2 - 16, `Dark Shard · ${darkShards}`, {
            fontSize: '13px',
            color: DARKSHARD_RED_HEX,
            fontStyle: 'bold',
          })
          .setOrigin(0.5),
      );
      box.on('pointerup', () => {
        if (this.busy) return;
        this.showDarkShardDialog();
      });
      offset += 1;
    }
    // Light Shards: one per Sky Islands win — stacked like the Dark Shard.
    if (lightShards > 0) {
      const { x, y } = cell(offset);
      const box = slotBox(offset, 'storage-lightshard', LIGHTSHARD_WHITE);
      const glow = this.add
        .rectangle(x, y, CELL - 6, CELL - 6, LIGHTSHARD_WHITE, 0.12)
        .setStrokeStyle(4, LIGHTSHARD_WHITE, 0.35);
      layer.add(glow);
      if (this.textures.exists('item-lightshard')) {
        layer.add(this.add.image(x, y - 10, 'item-lightshard').setDisplaySize(90, 90));
      }
      layer.add(
        this.add
          .text(x, y + CELL / 2 - 16, `Light Shard · ${lightShards}`, {
            fontSize: '13px',
            color: LIGHTSHARD_WHITE_HEX,
            fontStyle: 'bold',
          })
          .setOrigin(0.5),
      );
      box.on('pointerup', () => {
        if (this.busy) return;
        this.showLightShardDialog();
      });
      offset += 1;
    }
    // Space Debris: ONE slot however many pieces are held, with the count
    // badged on it — a shelf of nine identical slabs would tell you nothing
    // the badge does not.
    if (debris > 0) {
      const { x, y } = cell(offset);
      const box = slotBox(offset, 'storage-debris', DEBRIS_STEEL);
      if (this.textures.exists('item-debris')) {
        layer.add(this.add.image(x, y - 10, 'item-debris').setDisplaySize(90, 90));
      }
      layer.add(
        this.add
          .text(x, y + CELL / 2 - 16, `Space Debris · ${debris}`, {
            fontSize: '13px',
            color: DEBRIS_STEEL_HEX,
            fontStyle: 'bold',
          })
          .setOrigin(0.5),
      );
      box.on('pointerup', () => {
        if (this.busy) return;
        this.showDebrisDialog();
      });
      offset += 1;
    }
    // Golden Takeout Bags: ONE slot with the count badged on it, like the
    // capsules — they are all interchangeable (spec 2026-09-26).
    if (goldenBags > 0) {
      const { x, y } = cell(offset);
      const box = slotBox(offset, 'storage-goldenbag', GOLDEN_BAG_GOLD);
      layer.add(this.add.image(x, y - 10, 'takeout-golden').setDisplaySize(96, 96));
      layer.add(
        this.add
          .text(x, y + CELL / 2 - 16, `Golden · ${goldenBags}`, {
            fontSize: '13px',
            color: GOLDEN_BAG_GOLD_HEX,
            fontStyle: 'bold',
          })
          .setOrigin(0.5),
      );
      box.on('pointerup', () => {
        if (this.busy) return;
        this.showGoldenBagDialog();
      });
      offset += 1;
    }
    // Capsules: ONE slot with the count badged on it, like Space Debris —
    // every capsule on the shelf is interchangeable.
    if (capsules > 0) {
      const { x, y } = cell(offset);
      const box = slotBox(offset, 'storage-capsule', CAPSULE_BROWN);
      if (this.textures.exists('capsule-capy')) {
        layer.add(this.add.image(x, y - 10, 'capsule-capy').setDisplaySize(90, 90));
      }
      layer.add(
        this.add
          .text(x, y + CELL / 2 - 16, `Capsule · ${capsules}`, {
            fontSize: '13px',
            color: CAPSULE_BROWN_HEX,
            fontStyle: 'bold',
          })
          .setOrigin(0.5),
      );
      box.on('pointerup', () => {
        if (this.busy) return;
        this.showCapsuleDialog();
      });
      offset += 1;
    }
    potions.forEach((kind, i) => {
      if (offset >= COLS * MAX_ROWS) return;
      const slot = offset;
      const { x, y } = cell(slot);
      const def = POTIONS[kind];
      const box = slotBox(slot, `storage-potion-${i}`, POTION_GREEN);
      if (this.textures.exists(def.art)) {
        layer.add(this.add.image(x, y - 10, def.art).setDisplaySize(90, 90));
      }
      layer.add(
        this.add
          .text(x, y + CELL / 2 - 16, def.name, { fontSize: '13px', color: POTION_GREEN_HEX })
          .setOrigin(0.5),
      );
      box.on('pointerup', () => {
        if (this.busy) return;
        this.showPotionDialog(i, kind);
      });
      offset += 1;
    });
    if (hasOrb) {
      const { x, y } = cell(offset);
      const box = slotBox(offset, 'storage-orb', ORB_PURPLE);
      layer.add(this.add.image(x, y - 10, 'orb-variety').setDisplaySize(96, 96));
      layer.add(
        this.add
          .text(x, y + CELL / 2 - 16, 'Variety Orb', { fontSize: '13px', color: '#e0c8ff' })
          .setOrigin(0.5),
      );
      layer.add(this.rarityTag(x, y, 'storage-orb-rarity'));
      box.on('pointerup', () => {
        if (this.busy) return;
        this.showOrbDialog();
      });
      offset += 1;
    }
    // The Evil Orb (spec 2026-10-07): Ketchuptastic, and it does nothing yet.
    if (hasEvilOrb) {
      const { x, y } = cell(offset);
      const box = slotBox(offset, 'storage-evilorb', EVIL_ORB_ORANGE);
      if (this.textures.exists('item-evilorb')) {
        layer.add(this.add.image(x, y - 6, 'item-evilorb').setDisplaySize(86, 86));
      }
      layer.add(
        this.add
          .text(x, y + CELL / 2 - 16, 'Evil Orb', { fontSize: '13px', color: '#ffb48a' })
          .setOrigin(0.5),
      );
      layer.add(this.rarityTag(x, y, 'storage-evilorb-rarity'));
      box.on('pointerup', () => {
        if (this.busy) return;
        this.showEvilOrbDialog();
      });
      offset += 1;
    }
    // The Light Orb (spec 2026-10-13): the Evil Orb's twin from Heaven's Peak.
    if (hasLightOrb) {
      const { x, y } = cell(offset);
      const box = slotBox(offset, 'storage-lightorb', LIGHT_ORB_BLUE);
      if (this.textures.exists('decor-heavenorb')) {
        layer.add(this.add.image(x, y - 6, 'decor-heavenorb').setDisplaySize(86, 86));
      }
      layer.add(
        this.add
          .text(x, y + CELL / 2 - 16, 'Light Orb', { fontSize: '13px', color: '#e6f6ff' })
          .setOrigin(0.5),
      );
      layer.add(this.rarityTag(x, y, 'storage-lightorb-rarity'));
      box.on('pointerup', () => {
        if (this.busy) return;
        this.showEvilOrbDialog('light');
      });
      offset += 1;
    }
    // The Token of Shroom (spec 2026-10-27): Ketchuptastic, does nothing yet.
    if (hasToken) {
      const { x, y } = cell(offset);
      const box = slotBox(offset, 'storage-tokenofshroom', TOKEN_MOSS);
      if (this.textures.exists('item-tokenofshroom')) {
        layer.add(this.add.image(x, y - 6, 'item-tokenofshroom').setDisplaySize(86, 86));
      }
      layer.add(
        this.add
          .text(x, y + CELL / 2 - 16, 'Token of Shroom', { fontSize: '13px', color: '#d8f08a' })
          .setOrigin(0.5),
      );
      layer.add(this.rarityTag(x, y, 'storage-tokenofshroom-rarity'));
      box.on('pointerup', () => {
        if (this.busy) return;
        this.showEvilOrbDialog('token');
      });
      offset += 1;
    }
    // The Origin chests (spec 2026-10-13): ONE slot per kind with the count
    // badged on it — every chest of a kind is interchangeable.
    const chestSlot = (kind: ChestKind, count: number) => {
      if (count <= 0) return;
      const { x, y } = cell(offset);
      const stroke = kind === 'dark' ? DARK_CHEST_PURPLE : LIGHT_CHEST_CREAM;
      const box = slotBox(offset, `storage-${kind}chest`, stroke);
      if (this.textures.exists(CHEST_ART[kind])) {
        layer.add(this.add.image(x, y - 10, CHEST_ART[kind]).setDisplaySize(90, 90));
      }
      layer.add(
        this.add
          .text(x, y + CELL / 2 - 16, `${CHEST_NAMES[kind]} · ${count}`, {
            fontSize: '13px',
            color: kind === 'dark' ? DARK_CHEST_PURPLE_HEX : LIGHT_CHEST_CREAM_HEX,
            fontStyle: 'bold',
          })
          .setOrigin(0.5),
      );
      box.on('pointerup', () => {
        if (this.busy) return;
        this.showChestDialog(kind);
      });
      offset += 1;
    };
    chestSlot('dark', darkChests);
    chestSlot('light', lightChests);
    // The Fungi Forest's items (spec 2026-10-24): one slot per kind, badged.
    const fungiSlot = (kind: FungiItemKind, count: number) => {
      if (count <= 0 || offset >= COLS * MAX_ROWS) return;
      const def = FUNGI_ITEMS[kind];
      const { x, y } = cell(offset);
      const stroke = kind === 'shroom' ? SHROOM_RED : SNOWSHROOM_ICE;
      const box = slotBox(offset, `storage-${kind}`, stroke);
      if (this.textures.exists(def.art)) {
        layer.add(this.add.image(x, y - 10, def.art).setDisplaySize(90, 90));
      }
      layer.add(
        this.add
          .text(x, y + CELL / 2 - 16, `${def.name} · ${count}`, {
            fontSize: '13px',
            color: kind === 'shroom' ? SHROOM_RED_HEX : SNOWSHROOM_ICE_HEX,
            fontStyle: 'bold',
          })
          .setOrigin(0.5),
      );
      box.on('pointerup', () => {
        if (this.busy) return;
        this.showFungiItemDialog(kind);
      });
      offset += 1;
    };
    fungiSlot('shroom', shrooms);
    fungiSlot('snowshroom', snowshrooms);

    const shown = Math.max(0, Math.min(total, COLS * MAX_ROWS - offset));
    for (let i = 0; i < shown; i++) {
      const { x, y } = cell(i + offset);
      const box = slotBox(i + offset, `storage-bag-${i}`, GOLD);
      layer.add(this.add.image(x, y - 10, 'takeout-normal').setDisplaySize(96, 96));
      layer.add(
        this.add
          .text(x, y + CELL / 2 - 16, 'Normal', { fontSize: '13px', color: '#c8d2d8' })
          .setOrigin(0.5),
      );
      box.on('pointerup', () => {
        if (this.busy) return;
        this.showBagDialog();
      });
    }

    if (total > shown) {
      layer.add(
        this.add
          .text(640, GRID_TOP + MAX_ROWS * (CELL + GAP_Y) + 16, `+ ${total - shown} more`, {
            fontSize: '16px',
            color: '#9aa6ad',
            fontStyle: 'italic',
          })
          .setOrigin(0.5),
      );
    }
  }

  /**
   * USE / CANCEL for a Shroom or a Snowshroom. USE takes it off the shelf and
   * opens the tower picker; cancelling there puts it back.
   */
  private showFungiItemDialog(kind: FungiItemKind): void {
    const def = FUNGI_ITEMS[kind];
    this.busy = true;
    showUseDialog(this, {
      title: def.name,
      subtitle: `${def.effect} · lasts ${formatRemaining(def.durationMs)} from the moment you use it. You pick the tower next.`,
      accent: kind === 'shroom' ? SHROOM_RED : SNOWSHROOM_ICE,
      art: def.art,
      secondLabel: 'CANCEL',
      useName: `storage-${kind}-use`,
      secondName: 'storage-cancel',
      onUse: (close) => {
        close();
        this.busy = false;
        if (!this.progress.takeFungiItem(kind)) {
          this.render();
          return;
        }
        this.scene.start('TowerPickScene', {
          kind,
          returnTo: 'StorageScene',
          returnData: { from: this.from },
        });
      },
      onSecond: (close) => {
        close();
        this.busy = false;
      },
    });
  }

  /** OPEN / CANCEL for the tapped bag. Every bag in Storage is interchangeable. */
  private showBagDialog(): void {
    this.showItemDialog({
      title: 'Normal Takeout Bag',
      subtitle: 'Open it now, or leave it on the shelf.',
      accent: GOLD,
      onOpen: (close) => {
        // Take the bag off the shelf first, so the roll can only ever spend one.
        if (!this.progress.takeStoredBag()) {
          close();
          this.busy = false; // nothing opened — the shelf must stay clickable
          this.render();
          return;
        }
        close();
        this.render();
        openTakeoutBag(this, {
          progress: this.progress,
          onDone: () => {
            this.busy = false;
            this.balanceText.setText(coinLabel(this.progress.coins()));
            this.render();
          },
        });
      },
    });
  }

  /**
   * OPEN / CANCEL for a stored Origin chest. OPEN takes it off the shelf,
   * rolls and PAYS the prize, and only then plays the shake and the burst —
   * so a tab closed mid-animation still keeps what was inside.
   */
  private showChestDialog(kind: ChestKind): void {
    this.showItemDialog({
      title: CHEST_NAMES[kind],
      subtitle:
        kind === 'dark'
          ? 'Inside: 2 Dark Shards or 50 Dark Hambi Coins.'
          : 'Inside: 2 Light Shards or 50 Light Hambi Coins.',
      accent: kind === 'dark' ? DARK_CHEST_PURPLE : LIGHT_CHEST_CREAM,
      onOpen: (close) => {
        if (!this.progress.takeChest(kind)) {
          close();
          this.busy = false; // nothing opened — the shelf must stay clickable
          this.render();
          return;
        }
        const prize = rollChestPrize(chestRandom());
        this.progress.payChestPrize(kind, prize);
        close();
        this.render();
        openChest(this, {
          kind,
          prize,
          onDone: () => {
            this.busy = false;
            this.render();
          },
        });
      },
    });
  }

  /** OPEN / CANCEL for a stored Golden bag. They are all interchangeable. */
  private showGoldenBagDialog(): void {
    this.showItemDialog({
      title: 'Golden Takeout Bag',
      subtitle: 'Open it now, or leave it on the shelf.',
      accent: GOLDEN_BAG_GOLD,
      onOpen: (close) => {
        // Off the shelf first, so the roll can only ever spend one.
        if (!this.progress.takeStoredGoldenBag()) {
          close();
          this.busy = false; // nothing opened — the shelf must stay clickable
          this.render();
          return;
        }
        close();
        this.render();
        openGoldenBag(this, {
          progress: this.progress,
          onDone: () => {
            this.busy = false;
            this.balanceText.setText(coinLabel(this.progress.coins()));
            this.render();
          },
        });
      },
    });
  }

  /** OPEN / CANCEL for a stored capsule. They are all interchangeable. */
  private showCapsuleDialog(): void {
    this.showItemDialog({
      title: 'Capybara Capsule',
      subtitle: 'Open it now, or leave it on the shelf.',
      accent: CAPSULE_BROWN,
      onOpen: (close) => {
        // Off the shelf first, so the roll can only ever spend one.
        if (!this.progress.takeStoredCapsule()) {
          close();
          this.busy = false; // nothing opened — the shelf must stay clickable
          this.render();
          return;
        }
        close();
        this.render();
        openCapsule(this, {
          progress: this.progress,
          onDone: () => {
            this.busy = false;
            this.balanceText.setText(coinLabel(this.progress.coins()));
            this.render();
          },
        });
      },
    });
  }

  /** USE / CANCEL for the Diamond: a minute of shielded towers, from now. */
  private showDiamondDialog(): void {
    this.busy = true;
    showUseDialog(this, {
      title: 'The Diamond',
      subtitle:
        'Legendary. Use it and every tower you field takes no damage for 1:00 — starting the moment you press USE.',
      accent: LEGENDARY_YELLOW,
      art: 'diamond',
      secondLabel: 'CANCEL',
      useName: 'storage-diamond-use',
      secondName: 'storage-cancel',
      onUse: (close) => {
        close();
        this.busy = false;
        // Off the shelf first, so a double-tap cannot start two shields.
        if (this.progress.useDiamond()) {
          this.progress.startEffect('diamond', undefined, Date.now());
          flashHint(this, 'The Diamond shines — your towers are shielded for 1:00.', '#9fd8ff');
        }
        this.render();
      },
      onSecond: (close) => {
        close();
        this.busy = false;
      },
    });
  }

  /** USE / CANCEL for the Moon Stone: half an hour of +50% on every tower. */
  private showMoonstoneDialog(): void {
    this.busy = true;
    showUseDialog(this, {
      title: 'The Moon Stone',
      subtitle:
        'Legendary, and there is only one. Use it and a moon rises over every tower you field — all of them hit 50% harder for 30:00, starting the moment you press USE.',
      accent: MOONSTONE_VIOLET,
      art: 'item-moonstone',
      secondLabel: 'CANCEL',
      useName: 'storage-moonstone-use',
      secondName: 'storage-cancel',
      onUse: (close) => {
        close();
        this.busy = false;
        // Off the shelf first, so a double-tap cannot start two moons.
        if (this.progress.useMoonstone()) {
          this.progress.startEffect('moonstone', undefined, Date.now());
          flashHint(this, 'The Moon Stone glows — every tower hits harder for 30:00.', '#c3aaff');
        }
        this.render();
      },
      onSecond: (close) => {
        close();
        this.busy = false;
      },
    });
  }

  /** USE / CANCEL for a Golden Stone: the Moon Stone's half hour, in gold. */
  private showGoldstoneDialog(): void {
    this.busy = true;
    showUseDialog(this, {
      title: 'The Golden Stone',
      subtitle:
        'The Moon Stone, in gold — King Hambi pays one every time he casts his K–O. Use it and every tower you field hits 50% harder for 30:00, starting the moment you press USE.',
      accent: GOLDSTONE_GOLD,
      art: 'item-goldstone',
      secondLabel: 'CANCEL',
      useName: 'storage-goldstone-use',
      secondName: 'storage-cancel',
      onUse: (close) => {
        close();
        this.busy = false;
        // ASK FIRST, as for debris: the stones stack, and taking one off the
        // shelf before `startEffect` refuses a duplicate would waste it.
        const now = Date.now();
        const check = canApply(this.progress.effects(), 'goldstone', undefined, now);
        if (!check.ok) {
          flashHint(this, check.reason);
        } else if (this.progress.takeGoldstone()) {
          this.progress.startEffect('goldstone', undefined, now);
          flashHint(
            this,
            'The Golden Stone glows — every tower hits harder for 30:00.',
            GOLDSTONE_GOLD_HEX,
          );
        }
        this.render();
      },
      onSecond: (close) => {
        close();
        this.busy = false;
      },
    });
  }

  /** USE / CANCEL for a Capystone: the Moon Stone's half hour, in brown. */
  private showCapystoneDialog(): void {
    this.busy = true;
    showUseDialog(this, {
      title: 'The Capystone',
      subtitle:
        'The Moon Stone, in capybara brown — the Event Archive pays one for every Capy Run you finish. Use it and every tower you field hits 50% harder for 30:00, starting the moment you press USE.',
      accent: CAPYSTONE_BROWN,
      art: 'item-capystone',
      secondLabel: 'CANCEL',
      useName: 'storage-capystone-use',
      secondName: 'storage-cancel',
      onUse: (close) => {
        close();
        this.busy = false;
        // ASK FIRST, as for the Golden Stone: a refused duplicate must not
        // have already taken the stone off the shelf.
        const now = Date.now();
        const check = canApply(this.progress.effects(), 'capystone', undefined, now);
        if (!check.ok) {
          flashHint(this, check.reason);
        } else if (this.progress.takeCapystone()) {
          this.progress.startEffect('capystone', undefined, now);
          flashHint(
            this,
            'The Capystone glows — every tower hits harder for 30:00.',
            CAPYSTONE_BROWN_HEX,
          );
        }
        this.render();
      },
      onSecond: (close) => {
        close();
        this.busy = false;
      },
    });
  }

  /** The KETCHUPTASTIC band across the top of an orb's slot. */
  private rarityTag(x: number, y: number, name: string): Phaser.GameObjects.Text {
    return this.add
      .text(x, y - CELL / 2 + 14, 'KETCHUPTASTIC', {
        fontSize: '12px',
        color: KETCHUP_HEX,
        fontStyle: 'bold',
        stroke: '#fff4d6',
        strokeThickness: 2,
      })
      .setOrigin(0.5)
      .setName(name);
  }

  /**
   * USE / CANCEL for a Light Shard — the Dark Shard's +10% for two hours of
   * play, as its own effect, so the two run side by side.
   */
  private showLightShardDialog(): void {
    this.busy = true;
    showUseDialog(this, {
      title: 'The Light Shard',
      subtitle:
        'A white crystal from The Sky Islands. Use it and every tower you field hits 10% harder for 2:00:00 — and the clock only runs while you are playing.',
      accent: LIGHTSHARD_WHITE,
      art: 'item-lightshard',
      secondLabel: 'CANCEL',
      useName: 'storage-lightshard-use',
      secondName: 'storage-cancel',
      onUse: (close) => {
        close();
        this.busy = false;
        const now = Date.now();
        const check = canApply(this.progress.effects(), 'lightshard', undefined, now);
        if (!check.ok) {
          flashHint(this, check.reason);
        } else if (this.progress.takeLightShard()) {
          this.progress.startEffect('lightshard', undefined, now);
          flashHint(
            this,
            'The Light Shard shines — every tower hits harder for 2:00:00 of play.',
            LIGHTSHARD_WHITE_HEX,
          );
        }
        this.render();
      },
      onSecond: (close) => {
        close();
        this.busy = false;
      },
    });
  }

  /**
   * USE / CANCEL for a Dark Shard: every tower +10% for two hours — and the
   * two hours only run while the game is open (config/effects
   * OFFLINE_PAUSED_EFFECTS).
   */
  private showDarkShardDialog(): void {
    this.busy = true;
    showUseDialog(this, {
      title: 'The Dark Shard',
      subtitle:
        'A red crystal from The Dark Forest. Use it and every tower you field hits 10% harder for 2:00:00 — and the clock only runs while you are playing.',
      accent: DARKSHARD_RED,
      art: 'item-darkshard',
      secondLabel: 'CANCEL',
      useName: 'storage-darkshard-use',
      secondName: 'storage-cancel',
      onUse: (close) => {
        close();
        this.busy = false;
        // ASK FIRST, as for the stones: a refused duplicate must not have
        // already taken the shard off the shelf.
        const now = Date.now();
        const check = canApply(this.progress.effects(), 'darkshard', undefined, now);
        if (!check.ok) {
          flashHint(this, check.reason);
        } else if (this.progress.takeDarkShard()) {
          this.progress.startEffect('darkshard', undefined, now);
          flashHint(
            this,
            'The Dark Shard glows — every tower hits harder for 2:00:00 of play.',
            DARKSHARD_RED_HEX,
          );
        }
        this.render();
      },
      onSecond: (close) => {
        close();
        this.busy = false;
      },
    });
  }

  /** The Evil Orb's card: Ketchuptastic, and nothing to do with it — yet. */
  private showEvilOrbDialog(which: 'evil' | 'light' | 'token' = 'evil'): void {
    const light = which === 'light';
    const token = which === 'token';
    const accent = token ? TOKEN_MOSS : light ? LIGHT_ORB_BLUE : EVIL_ORB_ORANGE;
    const art = token ? 'item-tokenofshroom' : light ? 'decor-heavenorb' : 'item-evilorb';
    const W = 1280;
    const H = 720;
    const D = 200;
    this.busy = true;
    const objs: Phaser.GameObjects.GameObject[] = [];
    const add = <T extends Phaser.GameObjects.GameObject>(o: T): T => {
      objs.push(o);
      return o;
    };
    add(
      this.add
        .rectangle(W / 2, H / 2, W, H, 0x000000, 0.7)
        .setInteractive()
        .setDepth(D),
    );
    add(
      this.add
        .rectangle(W / 2, H / 2, 600, 320, 0x1a1726, 1)
        .setStrokeStyle(2, accent)
        .setDepth(D + 1),
    );
    if (this.textures.exists(art)) {
      add(
        this.add
          .image(W / 2 - 200, H / 2 - 30, art)
          .setDisplaySize(120, 120)
          .setDepth(D + 2),
      );
    }
    add(
      this.add
        .text(W / 2 + 60, H / 2 - 110, 'KETCHUPTASTIC', {
          fontSize: '20px',
          color: KETCHUP_HEX,
          fontStyle: 'bold',
          stroke: '#fff4d6',
          strokeThickness: 3,
        })
        .setOrigin(0.5)
        .setDepth(D + 2),
    );
    add(
      this.add
        .text(
          W / 2 + 60,
          H / 2 - 72,
          token ? 'Token of Shroom' : light ? 'The Light Orb' : 'The Evil Orb',
          {
            fontSize: '28px',
            color: '#ffffff',
            fontStyle: 'bold',
          },
        )
        .setOrigin(0.5)
        .setDepth(D + 2),
    );
    add(
      this.add
        .text(
          W / 2 + 60,
          H / 2 - 4,
          token
            ? 'Won from King Shroometron in The Shroombellion. A gold coin grown over with moss... it does nothing — yet.'
            : light
              ? "Lifted from the sakura ring on Heaven's Peak. Its pale light never fades... but it does nothing — yet."
              : 'Taken from The Shadow Reaper in The Quest for the Evil Orb. Its red glow never fades... but it does nothing — yet.',
          { fontSize: '16px', color: '#9aa6ad', wordWrap: { width: 330 }, align: 'center' },
        )
        .setOrigin(0.5)
        .setDepth(D + 2),
    );
    const close = add(
      this.add
        .rectangle(W / 2, H / 2 + 104, 200, 56, 0x2a2a3a)
        .setStrokeStyle(2, 0x6666aa)
        .setInteractive({ useHandCursor: true })
        .setDepth(D + 2)
        .setName(
          token
            ? 'storage-tokenofshroom-close'
            : light
              ? 'storage-lightorb-close'
              : 'storage-evilorb-close',
        ),
    );
    add(
      this.add
        .text(W / 2, H / 2 + 104, 'CLOSE', {
          fontSize: '22px',
          color: '#ffffff',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(D + 3),
    );
    close.on('pointerup', () => {
      objs.forEach((o) => o.destroy());
      this.busy = false;
    });
  }

  /** USE / CANCEL for Space Debris: two minutes with every nacho jammed. */
  private showDebrisDialog(): void {
    this.busy = true;
    showUseDialog(this, {
      title: 'Space Debris',
      subtitle:
        'A slab of blackened hull off the rocket. Use it and no nacho can touch your towers for 2:00 — no knives, no lasers, no stuns — starting the moment you press USE.',
      accent: DEBRIS_STEEL,
      art: 'item-debris',
      secondLabel: 'CANCEL',
      useName: 'storage-debris-use',
      secondName: 'storage-cancel',
      onUse: (close) => {
        close();
        this.busy = false;
        // ASK FIRST. Debris stacks, unlike the Diamond, so a player can hold a
        // second piece while the first still runs — and `startEffect` refuses
        // a duplicate. Taking one off the shelf before that verdict would
        // destroy it for nothing.
        const now = Date.now();
        const check = canApply(this.progress.effects(), 'debris', undefined, now);
        if (!check.ok) {
          flashHint(this, check.reason);
        } else if (this.progress.takeDebris()) {
          this.progress.startEffect('debris', undefined, now);
          flashHint(this, 'The nachos are jammed for 2:00.', '#cfe4ff');
        }
        this.render();
      },
      onSecond: (close) => {
        close();
        this.busy = false;
      },
    });
  }

  /** USE / CANCEL for a stored potion. */
  private showPotionDialog(index: number, kind: PotionKind): void {
    const def = POTIONS[kind];
    this.busy = true;
    showUseDialog(this, {
      title: def.name,
      subtitle: `${def.effect} · lasts ${formatRemaining(def.durationMs)} from the moment you use it.${
        def.targeted ? ' You pick the tower next.' : ''
      }`,
      accent: POTION_GREEN,
      art: def.art,
      secondLabel: 'CANCEL',
      useName: 'storage-potion-use',
      secondName: 'storage-cancel',
      onUse: (close) => {
        close();
        this.busy = false;
        // Off the shelf first; a targeted potion goes back on if the pick is cancelled.
        if (this.progress.takeStoredPotion(index) !== kind) {
          this.render();
          return;
        }
        const r = usePotion(this, kind, 'StorageScene', { from: this.from });
        if (r.outcome === 'started') flashHint(this, `${def.name} is active!`, '#7ed67e');
        else if (r.outcome === 'refused') flashHint(this, r.reason ?? 'Already active');
        if (r.outcome !== 'picking') this.render();
      },
      onSecond: (close) => {
        close();
        this.busy = false;
      },
    });
  }

  /** OPEN / CANCEL for the Variety Orb. There is only ever one. */
  private showOrbDialog(): void {
    this.showItemDialog({
      title: 'The Variety Orb',
      subtitle: 'KETCHUPTASTIC · one tower inside, any but your starting ones.',
      accent: ORB_PURPLE,
      onOpen: (close) => {
        // Off the shelf BEFORE the roll, exactly as a bag is: `openOrb` returns
        // false on a second call, so a double-tap cannot roll two towers.
        if (!this.progress.openOrb()) {
          close();
          this.busy = false; // nothing opened — the shelf must stay clickable
          this.render();
          return;
        }
        close();
        this.render();
        openVarietyOrb(this, {
          progress: this.progress,
          onDone: () => {
            this.busy = false;
            this.balanceText.setText(coinLabel(this.progress.coins()));
            this.render();
          },
        });
      },
    });
  }

  /**
   * The shared OPEN / CANCEL modal. `onOpen` is handed a `close` that disposes
   * the dialog's own objects WITHOUT clearing `busy` — the caller keeps the
   * scene busy across the opening ceremony that follows and clears it in its
   * own `onDone`, which is what stops a second item being opened underneath.
   */
  private showItemDialog(opts: {
    title: string;
    subtitle: string;
    accent: number;
    onOpen: (close: () => void) => void;
  }): void {
    const W = 1280;
    const H = 720;
    const D = 200;
    this.busy = true;
    const objs: Phaser.GameObjects.GameObject[] = [];

    objs.push(
      this.add
        .rectangle(W / 2, H / 2, W, H, 0x000000, 0.7)
        .setInteractive()
        .setDepth(D),
    );
    objs.push(
      this.add
        .rectangle(W / 2, H / 2, 560, 280, 0x1a1726, 1)
        .setStrokeStyle(2, opts.accent)
        .setDepth(D + 1),
    );
    objs.push(
      this.add
        .text(W / 2, H / 2 - 86, opts.title, {
          fontSize: '28px',
          color: '#ffffff',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(D + 2),
    );
    objs.push(
      this.add
        .text(W / 2, H / 2 - 44, opts.subtitle, {
          fontSize: '16px',
          color: '#9aa6ad',
        })
        .setOrigin(0.5)
        .setDepth(D + 2),
    );

    const teardown = () => {
      objs.forEach((o) => o.destroy());
      this.busy = false;
    };

    const mk = (dx: number, text: string, fill: number, stroke: number, name: string) => {
      const box = this.add
        .rectangle(W / 2 + dx, H / 2 + 62, 200, 56, fill)
        .setStrokeStyle(2, stroke)
        .setInteractive({ useHandCursor: true })
        .setDepth(D + 2)
        .setName(name);
      objs.push(box);
      objs.push(
        this.add
          .text(W / 2 + dx, H / 2 + 62, text, {
            fontSize: '22px',
            color: '#ffffff',
            fontStyle: 'bold',
          })
          .setOrigin(0.5)
          .setDepth(D + 3),
      );
      return box;
    };

    mk(-114, 'OPEN', 0x1a6b1a, 0x4caf50, 'storage-open').on('pointerup', () => {
      opts.onOpen(() => objs.forEach((o) => o.destroy()));
    });
    mk(114, 'CANCEL', 0x2a2a3a, 0x6666aa, 'storage-cancel').on('pointerup', teardown);
  }
}
